/**
 * Vista previa proforma del cierre. Pensada para el momento entre
 * CALCULADO y FACTURADO — la admin ve lo que va a facturar (sumarios,
 * alertas, lista completa con drill-down) antes de emitir las boletas.
 */
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  Droplet,
  FileCheck2,
  Search,
  TrendingDown,
  TrendingUp,
  X,
} from "lucide-react";

import { ApiError, formatCLP } from "../../lib/api";
import { cantidad } from "../../lib/format";
import { useAvisoSalida } from "../../lib/useAvisoSalida";
import { useToast } from "../../components/Toast";
import {
  Badge,
  type BadgeTone,
  Button,
  Card,
  Cargando,
  ConfirmDialog,
  ErrorCarga,
  PageHeader,
  TableWrap,
} from "../../components/ui";
import { GrupoAvisos } from "./GrupoAvisos";
import { descuentoCLP } from "./textos";
import {
  facturarCierre,
  obtenerProforma,
  type AlertaProforma,
  type AlertaProformaCodigo,
  type AlertaSeveridad,
  type EmpresaInfoProforma,
  type LecturaProforma,
  type ProformaResponse,
} from "./api";
import { enCurso, registrarTareaLanzada, useTareaCierre } from "./tareas";
import { ETIQUETA_ESTADO, MESES } from "./estado";

const SEVERIDAD_TONO: Record<AlertaSeveridad, BadgeTone> = {
  info: "slate",
  warn: "amber",
  danger: "rose",
};

const PESO_SEVERIDAD: Record<AlertaSeveridad, number> = { danger: 0, warn: 1, info: 2 };

/**
 * Cómo se presenta cada tipo de aviso. `conDetalle`: la descripción trae
 * números propios de la casa (vale mostrarla); si no, repite el título.
 */
const TIPO_ALERTA: Record<
  AlertaProformaCodigo,
  { titulo: string; orden: number; conDetalle: boolean }
> = {
  MONTO_ALTO: { titulo: "boletas con monto inusual (más del doble del promedio)", orden: 0, conDetalle: true },
  CONSUMO_ALTO: { titulo: "consumos muy sobre el promedio de la casa", orden: 1, conDetalle: true },
  CONSUMO_CERO: { titulo: "casas con consumo cero", orden: 2, conDetalle: false },
  SIN_HISTORICO: { titulo: "casas sin consumos anteriores para comparar", orden: 3, conDetalle: false },
  ESTIMADA: { titulo: "lecturas estimadas por término medio", orden: 4, conDetalle: false },
};

interface GrupoAlerta {
  codigo: AlertaProformaCodigo;
  titulo: string;
  severidad: AlertaSeveridad;
  conDetalle: boolean;
  lecturas: { lectura: LecturaProforma; alerta: AlertaProforma }[];
}

/** Una sección por tipo de aviso: lo grave primero, lo informativo al final. */
function agruparAlertas(lecturas: LecturaProforma[]): GrupoAlerta[] {
  const grupos = new Map<AlertaProformaCodigo, GrupoAlerta>();
  for (const l of lecturas) {
    for (const a of l.alertas) {
      let g = grupos.get(a.codigo);
      if (!g) {
        const tipo = TIPO_ALERTA[a.codigo] ?? { titulo: a.codigo, orden: 9, conDetalle: true };
        g = {
          codigo: a.codigo,
          titulo: tipo.titulo,
          severidad: a.severidad,
          conDetalle: tipo.conDetalle,
          lecturas: [],
        };
        grupos.set(a.codigo, g);
      }
      if (PESO_SEVERIDAD[a.severidad] < PESO_SEVERIDAD[g.severidad]) g.severidad = a.severidad;
      g.lecturas.push({ lectura: l, alerta: a });
    }
  }
  return [...grupos.values()].sort(
    (a, b) =>
      PESO_SEVERIDAD[a.severidad] - PESO_SEVERIDAD[b.severidad] ||
      (TIPO_ALERTA[a.codigo]?.orden ?? 9) - (TIPO_ALERTA[b.codigo]?.orden ?? 9),
  );
}

export function CierreProformaPage(): JSX.Element {
  const { cierreId: cierreIdParam } = useParams<{ cierreId: string }>();
  const cierreId = Number(cierreIdParam);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  const [filtro, setFiltro] = useState("");
  const [lecturaSeleccionada, setLecturaSeleccionada] = useState<LecturaProforma | null>(null);
  const [confirmEmitir, setConfirmEmitir] = useState(false);

  const proformaQ = useQuery({
    queryKey: ["cierre-proforma", cierreId],
    queryFn: () => obtenerProforma(cierreId),
    enabled: Number.isFinite(cierreId),
  });

  // Emitir corre en el servidor (2.000 boletas tardan minutos): el POST sólo
  // la lanza y el avance se sigue en el detalle del cierre, donde el panel de
  // la tarea refresca y avisa al terminar.
  const tareaQ = useTareaCierre(cierreId);
  const facturarMut = useMutation({
    mutationFn: () => facturarCierre(cierreId),
    onSettled: () => setConfirmEmitir(false),
    onSuccess: (tarea) => {
      registrarTareaLanzada(qc, tarea);
      toast.info("Emisión iniciada. Puedes seguir trabajando; el avance queda en el cierre.");
      navigate(`/cierres/${cierreId}`);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : "No se pudo iniciar la emisión");
      void tareaQ.refetch();
    },
  });

  const proforma = proformaQ.data;

  const lecturasFiltradas = useMemo(() => {
    if (!proforma) return [] as LecturaProforma[];
    if (!filtro.trim()) return proforma.lecturas;
    const q = filtro.trim().toLowerCase();
    return proforma.lecturas.filter(
      (l) =>
        l.socio_nombre.toLowerCase().includes(q) ||
        l.socio_rut.toLowerCase().includes(q) ||
        (l.arranque_codigo ?? "").toLowerCase().includes(q) ||
        l.arranque_direccion.toLowerCase().includes(q),
    );
  }, [proforma, filtro]);

  const lecturasConAlerta = useMemo(
    () => (proforma ? proforma.lecturas.filter((l) => l.alertas.length > 0) : []),
    [proforma],
  );
  const gruposAlerta = useMemo(() => agruparAlertas(lecturasConAlerta), [lecturasConAlerta]);
  const importantes = useMemo(
    () =>
      lecturasConAlerta.filter((l) => l.alertas.some((a) => a.severidad !== "info")).length,
    [lecturasConAlerta],
  );

  const emitiendo = facturarMut.isPending;
  // Hay una operación corriendo en este cierre (p. ej. la emisión lanzada
  // desde otra pestaña): no se ofrece emitir de nuevo.
  const tareaCorriendo = enCurso(tareaQ.data);
  // Sólo mientras se lanza: la emisión en sí ya no depende de esta pestaña.
  useAvisoSalida(emitiendo);

  if (!Number.isFinite(cierreId)) {
    return <p className="text-sm text-rose-700">Cierre inválido.</p>;
  }
  // PageHeader arma "Volver a {etiqueta}": con el nombre del cierre se lee
  // "Volver a Cierre Septiembre 2026" (antes "Volver a el cierre").
  const volverAlCierre = {
    etiqueta: proformaQ.data
      ? `Cierre ${MESES[proformaQ.data.mes - 1]} ${proformaQ.data.anio}`
      : "Cierre",
    ruta: `/cierres/${cierreId}`,
  };
  if (proformaQ.isPending) {
    return (
      <div className="space-y-6">
        <PageHeader title="Vista previa del cierre" volver={volverAlCierre} />
        <Cargando texto="Cargando proforma…" />
      </div>
    );
  }
  if (proformaQ.isError || !proforma) {
    return (
      <div className="space-y-6">
        <PageHeader title="Vista previa del cierre" volver={volverAlCierre} />
        <ErrorCarga
          error={proformaQ.error}
          mensaje="No se pudo cargar la proforma."
          onReintentar={() => void proformaQ.refetch()}
        />
      </div>
    );
  }

  const puedeFacturar = proforma.estado === "CALCULADO";
  const nBoletas = proforma.sumarios.boletas_a_emitir.toLocaleString("es-CL");

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Vista previa — ${MESES[proforma.mes - 1]} ${proforma.anio}`}
        subtitle="Revisa lo que se va a facturar. Una vez emitidas, las boletas llevan número y son difíciles de revertir."
        volver={volverAlCierre}
      />

      {/* Sumarios ejecutivos */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <ResumenCard label="Boletas a emitir" value={String(proforma.sumarios.boletas_a_emitir)} highlight />
        <ResumenCard
          label="Total a facturar"
          value={formatCLP(proforma.sumarios.total_facturado)}
          highlight
          delta={proforma.comparativa.delta_pct_facturado}
        />
        <ResumenCard
          label="Total m³"
          value={`${cantidad(proforma.sumarios.total_m3)} m³`}
          delta={proforma.comparativa.delta_pct_m3}
        />
        <ResumenCard
          label="Subsidios aplicados"
          value={descuentoCLP(proforma.sumarios.total_subsidios)}
        />
      </section>

      {proforma.comparativa.mes_anterior_existe && (
        <div className="text-xs text-neutral-500">
          Comparativa contra mes anterior: facturado{" "}
          {formatCLP(proforma.comparativa.mes_anterior_total_facturado)} ·{" "}
          {cantidad(proforma.comparativa.mes_anterior_total_m3)} m³ ·{" "}
          {cantidad(proforma.comparativa.mes_anterior_boletas)} boletas.
        </div>
      )}

      {/* Avisos agrupados por tipo: los importantes primero, y las cientos
          de estimadas en una sola línea plegable. */}
      {gruposAlerta.length > 0 && (
        <Card padding="md" className="space-y-3">
          <header className="flex flex-wrap items-center gap-2">
            <AlertTriangle
              className={`h-4 w-4 ${importantes > 0 ? "text-amber-700" : "text-neutral-500"}`}
              strokeWidth={1.5}
              aria-hidden="true"
            />
            <h3 className="text-sm font-semibold text-neutral-900">
              {importantes > 0
                ? `Necesitan tu atención (${importantes.toLocaleString("es-CL")})`
                : "Avisos de la vista previa"}
            </h3>
            <span className="text-xs text-neutral-600">
              {lecturasConAlerta.length.toLocaleString("es-CL")} boleta(s) con algún aviso
            </span>
          </header>
          <div className="space-y-2">
            {gruposAlerta.map((g) => (
              <GrupoAvisos
                key={g.codigo}
                titulo={g.titulo}
                cantidad={g.lecturas.length}
                tono={SEVERIDAD_TONO[g.severidad]}
                etiqueta={g.severidad === "info" ? "Aviso" : "Revisar"}
                abiertoInicial={g.severidad !== "info"}
                items={g.lecturas}
                itemKey={(x) => x.lectura.lectura_id}
                renderItem={({ lectura: l, alerta }) => (
                  <div className="flex flex-wrap items-start justify-between gap-2 text-sm">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-neutral-900">
                        {l.socio_nombre}{" "}
                        <span className="text-xs font-normal text-neutral-600">
                          · {l.arranque_codigo ?? `#${l.contrato_id}`} · {l.arranque_direccion}
                        </span>
                      </div>
                      <div className="mt-0.5 text-xs text-neutral-600">
                        {l.consumo_m3} m³ · {formatCLP(l.monto_total)}
                        {g.conDetalle && <> · {alerta.descripcion}</>}
                      </div>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="shrink-0"
                      onClick={() => setLecturaSeleccionada(l)}
                    >
                      Ver proforma
                    </Button>
                  </div>
                )}
              />
            ))}
          </div>
        </Card>
      )}

      {/* Tabla completa */}
      <Card padding="none" className="overflow-hidden">
        <header className="px-4 py-3 border-b border-neutral-100 flex flex-wrap items-center gap-3">
          <h3 className="text-sm font-semibold text-neutral-900">
            Todas las boletas ({proforma.sumarios.boletas_a_emitir})
          </h3>
          <div className="ml-auto relative w-full sm:w-64">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-500" strokeWidth={1.5} />
            <input
              type="search"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              placeholder="Buscar socio, RUT, arranque…"
              className="w-full pl-8 pr-3 py-1.5 text-xs rounded-md border border-neutral-450 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            />
          </div>
        </header>
        <TableWrap label="Todas las boletas" className="rounded-none ring-0">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Socio</th>
                <th scope="col" className="px-3 py-2 text-left">Arranque</th>
                <th scope="col" className="px-3 py-2 text-right">Consumo</th>
                <th scope="col" className="px-3 py-2 text-right">Total</th>
                <th scope="col" className="px-3 py-2 text-left">Alertas</th>
                <th scope="col" className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {lecturasFiltradas.map((l) => (
                <tr
                  key={l.lectura_id}
                  className={l.alertas.length > 0 ? "bg-amber-50/30" : ""}
                >
                  <td className="px-3 py-2">
                    <div className="font-medium text-neutral-900">{l.socio_nombre}</div>
                    <div className="text-xs text-neutral-500">{l.socio_rut}</div>
                  </td>
                  <td className="px-3 py-2 text-neutral-600 text-xs">
                    <div>{l.arranque_codigo ?? `#${l.contrato_id}`}</div>
                    <div className="text-neutral-500">{l.arranque_direccion}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs">
                    {l.consumo_m3} m³
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatCLP(l.monto_total)}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {l.alertas.map((a) => (
                        <AlertaBadge key={a.codigo} alerta={a} compact />
                      ))}
                      {l.alertas.length === 0 && (
                        <span className="text-xs text-neutral-500">—</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => setLecturaSeleccionada(l)}
                      className="text-xs font-medium text-brand-700 hover:text-brand-800"
                    >
                      Ver
                    </button>
                  </td>
                </tr>
              ))}
              {lecturasFiltradas.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-sm text-neutral-500">
                    Sin resultados para "{filtro}"
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </TableWrap>
      </Card>

      {/* CTA Aprobar y emitir */}
      {puedeFacturar && (
        <div className="sticky bottom-0 -mx-4 px-4 py-3 bg-white border-t border-neutral-200 flex flex-wrap items-center gap-3">
          <div className="text-sm text-neutral-700">
            <strong>Listo para emitir.</strong> Esta acción asigna número de boleta
            a {nBoletas} lecturas.
          </div>
          {/* Emitir siempre pasa por la confirmación con los números, y el
              diálogo queda abierto mientras el servidor numera las boletas. */}
          <Button
            icon={FileCheck2}
            className="ml-auto"
            onClick={() => setConfirmEmitir(true)}
            disabled={emitiendo || tareaCorriendo}
          >
            {emitiendo || tareaCorriendo
              ? "Emitiendo…"
              : `Aprobar y emitir ${nBoletas} boletas`}
          </Button>
        </div>
      )}

      <ConfirmDialog
        open={confirmEmitir}
        tone="default"
        title="Emitir boletas"
        description={
          <>
            Se emitirán {nBoletas} boletas por{" "}
            <strong>{formatCLP(proforma.sumarios.total_facturado)}</strong> del periodo{" "}
            {MESES[proforma.mes - 1]} {proforma.anio}. Cada una recibe su número de
            boleta y no se puede deshacer. La emisión corre en el servidor: puedes
            seguir trabajando y el avance queda en el cierre.
          </>
        }
        confirmLabel={`Emitir ${nBoletas} boletas`}
        loading={emitiendo}
        loadingLabel="Iniciando la emisión…"
        onConfirm={() => facturarMut.mutate()}
        onCancel={() => setConfirmEmitir(false)}
      />

      {!puedeFacturar && (
        <div className="text-xs text-neutral-500 italic">
          Vista de consulta: el cierre está en{" "}
          <strong>{ETIQUETA_ESTADO[proforma.estado]}</strong> y ya no se emite
          desde acá.
        </div>
      )}

      {lecturaSeleccionada && (
        <ProformaModal
          lectura={lecturaSeleccionada}
          empresa={proforma.empresa}
          mes={proforma.mes}
          anio={proforma.anio}
          onClose={() => setLecturaSeleccionada(null)}
        />
      )}
    </div>
  );
}

function ResumenCard({
  label,
  value,
  highlight,
  delta,
}: {
  label: string;
  value: string;
  highlight?: boolean;
  delta?: number | null | undefined;
}): JSX.Element {
  const showDelta = typeof delta === "number" && Number.isFinite(delta);
  const positivo = showDelta && delta! > 0;
  return (
    <div
      className={`rounded-lg border p-4 ${
        highlight
          ? "bg-brand-50 border-brand-200 text-brand-900"
          : "bg-white border-neutral-200 text-neutral-900"
      }`}
    >
      <div className="text-xs uppercase tracking-wider opacity-70">{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
      {showDelta && (
        <div
          className={`mt-1 text-xs flex items-center gap-1 ${
            positivo ? "text-emerald-700" : "text-rose-700"
          }`}
        >
          {positivo ? (
            <TrendingUp className="h-3 w-3" />
          ) : (
            <TrendingDown className="h-3 w-3" />
          )}
          {delta!.toFixed(1)}% vs mes anterior
        </div>
      )}
    </div>
  );
}

function AlertaBadge({
  alerta,
  compact,
}: {
  alerta: AlertaProforma;
  compact?: boolean;
}): JSX.Element {
  return (
    <span title={alerta.descripcion}>
      <Badge tone={SEVERIDAD_TONO[alerta.severidad]}>
        {compact ? alerta.codigo.replace("_", " ") : alerta.descripcion}
      </Badge>
    </span>
  );
}

/** Un tramo de `detalle_tramos` (backend: `TramoAplicado.to_dict`). */
interface TramoDetalle {
  desde?: number;
  hasta?: number | null;
  m3?: number;
  valor_unitario?: number;
  monto?: number;
}

function formatFecha(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
}

function ProformaModal({
  lectura,
  empresa,
  mes,
  anio,
  onClose,
}: {
  lectura: LecturaProforma;
  empresa: EmpresaInfoProforma;
  mes: number;
  anio: number;
  onClose: () => void;
}): JSX.Element {
  const tramos = useMemo<TramoDetalle[]>(() => {
    if (!lectura.detalle_tramos) return [];
    try {
      const parsed = JSON.parse(lectura.detalle_tramos);
      return Array.isArray(parsed) ? (parsed as TramoDetalle[]) : [];
    } catch {
      return [];
    }
  }, [lectura.detalle_tramos]);

  // La proforma no trae fecha de emisión (se fija al facturar): se muestra
  // la de lectura con su nombre.
  const fechaLectura = formatFecha(lectura.fecha_lectura);
  // Lo que el total trae y esta vista no desglosa (IVA de no socio, intereses,
  // corte/reposición, ajustes). Se muestra en una línea para que la suma cuadre.
  const otrosCargos =
    lectura.monto_total -
    (lectura.monto_cargo_fijo +
      lectura.monto_consumo +
      lectura.monto_alcantarillado -
      lectura.monto_subsidio +
      lectura.monto_multas);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-0 sm:p-4 bg-black/60"
      onClick={onClose}
    >
      <div
        className="w-full min-w-0 max-w-3xl max-h-[95vh] overflow-y-auto overflow-x-hidden bg-white shadow-2xl sm:rounded-lg relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Marca de agua "BORRADOR" (recortada: no debe ensanchar el modal) */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden select-none"
        >
          <span className="text-[120px] font-black text-rose-100 opacity-50 -rotate-12 tracking-widest">
            BORRADOR
          </span>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="absolute top-2 right-2 z-10 rounded-full bg-white border border-neutral-300 p-1.5 text-neutral-500 hover:text-neutral-900 hover:border-neutral-400"
          aria-label="Cerrar"
        >
          <X className="h-4 w-4" strokeWidth={1.5} />
        </button>

        <div className="relative min-w-0 p-4 sm:p-6 space-y-3 text-sm">
          {/* HEADER: comité + caja roja boleta */}
          <header className="flex flex-wrap sm:flex-nowrap items-start gap-3">
            <div className="h-14 w-14 rounded-full bg-neutral-100 flex items-center justify-center shrink-0">
              {empresa.logo_url ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={empresa.logo_url}
                  alt="Logo"
                  className="h-12 w-12 object-contain"
                />
              ) : (
                <Droplet
                  className="h-7 w-7 text-brand-700"
                  strokeWidth={1.5}
                  aria-hidden="true"
                />
              )}
            </div>
            <div className="min-w-0 flex-1 text-center">
              <h3 className="text-base font-bold text-brand-700 uppercase break-words">
                {empresa.nombre}
              </h3>
              <div className="text-xs text-neutral-600">
                Servicio Sanitario Rural
              </div>
              {empresa.direccion && (
                <div className="text-xs text-neutral-600">
                  Dirección: {empresa.direccion}
                </div>
              )}
              <div className="text-xs text-neutral-600">
                {empresa.telefono && <span>Fono: {empresa.telefono}</span>}
                {empresa.telefono && empresa.email && <span> | </span>}
                {empresa.email && <span>Email: {empresa.email}</span>}
              </div>
            </div>
            <div className="border-2 border-rose-600 px-3 py-2 text-center min-w-[160px]">
              <div className="text-xs font-bold text-rose-700">
                R.U.T.: {empresa.rut}
              </div>
              <div className="text-xs font-bold text-rose-700 mt-1 leading-tight">
                {lectura.es_socio ? (
                  <>
                    BOLETA EXENTA
                    <br />
                    ELECTRÓNICA
                  </>
                ) : (
                  "BOLETA ELECTRÓNICA"
                )}
              </div>
              <div className="text-base font-bold text-rose-700 mt-1">
                Nº ―
              </div>
              <div className="text-xs text-rose-700 font-semibold">
                S.I.I. - CHILE
              </div>
            </div>
          </header>

          {/* Banda BORRADOR explícita */}
          <div className="bg-rose-50 border border-rose-200 px-3 py-1.5 text-xs text-rose-800 text-center font-semibold uppercase tracking-wider">
            Vista previa — el número de boleta se asigna al facturar
          </div>

          {/* DATOS DEL CLIENTE */}
          <Section title="Datos del cliente">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs px-3 py-2">
              <Field label="Razón Social" value={lectura.socio_nombre.toUpperCase()} />
              <Field label="Sector" value={lectura.sector_nombre ?? "—"} />
              <Field label="R.U.T." value={lectura.socio_rut} />
              <Field label="Fec. Lectura" value={fechaLectura} />
              <Field
                label="Dirección"
                value={(lectura.socio_direccion ?? lectura.arranque_direccion).toUpperCase()}
              />
              <Field label="Estado" value="PROFORMA" valueClassName="font-semibold text-rose-700" />
            </div>
          </Section>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Detalle de su cuenta */}
            <Section title="Detalle de su cuenta">
              <div className="px-3 py-2 space-y-1 text-xs">
                <CuentaLine label="CARGO FIJO AGUA POTABLE" valor={lectura.monto_cargo_fijo} />
                <CuentaLine label="CONSUMO AGUA POTABLE" valor={lectura.monto_consumo} />
                {lectura.monto_alcantarillado > 0 && (
                  <CuentaLine label="ALCANTARILLADO" valor={lectura.monto_alcantarillado} />
                )}
                {lectura.monto_subsidio > 0 && (
                  <CuentaLine
                    label="SUBSIDIO APLICADO"
                    valor={-lectura.monto_subsidio}
                    negativo
                  />
                )}
                {lectura.monto_multas > 0 && (
                  <CuentaLine label="MULTAS / INTERESES" valor={lectura.monto_multas} />
                )}
                {otrosCargos !== 0 && (
                  <CuentaLine
                    label="IVA, INTERESES Y OTROS"
                    valor={otrosCargos}
                    negativo={otrosCargos < 0}
                  />
                )}
                <div className="border-t-2 border-neutral-300 pt-1.5 mt-1.5">
                  <CuentaLine label="TOTAL A PAGAR" valor={lectura.monto_total} highlight />
                </div>
              </div>
            </Section>

            <div className="space-y-3">
              {/* Número Contrato */}
              <div className="border border-neutral-300 px-3 py-2 flex items-center justify-between text-xs">
                <span>Número Contrato</span>
                <span className="font-bold text-base">{lectura.contrato_id}</span>
              </div>

              {/* Detalle del Suministro */}
              <Section title="Detalle del suministro">
                <div className="px-3 py-2 space-y-0.5 text-xs">
                  <SuministroLine label="Sector" value={lectura.sector_nombre ?? "—"} />
                  <SuministroLine
                    label="Direc. Med."
                    value={lectura.arranque_direccion}
                  />
                  <SuministroLine
                    label="Nº Medidor"
                    value={lectura.medidor_numero_serie ?? "—"}
                    extraLabel="Diámetro"
                    extraValue={lectura.medidor_diametro ?? "—"}
                  />
                </div>
              </Section>

              {/* Detalle del Consumo */}
              <Section
                title={`Detalle del consumo : ${(MESES[mes - 1] ?? "").toUpperCase()} ${anio}`}
              >
                <div className="px-3 py-2 space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span>
                      <strong>Lectura Actual:</strong> {lectura.lectura_actual} m³
                      <div className="text-neutral-500">{fechaLectura}</div>
                    </span>
                    <span>
                      <strong>Lectura Anterior:</strong> {lectura.lectura_anterior} m³
                    </span>
                  </div>
                  <div>
                    <strong>Consumo Total:</strong> {lectura.consumo_m3} m³
                  </div>
                  <div className="flex justify-between text-xs pt-1 border-t border-neutral-100">
                    <span>* Consumo (M3): {lectura.consumo_m3}</span>
                    <span>* Tipo: {lectura.tipo_lectura}</span>
                  </div>
                </div>
              </Section>
            </div>
          </div>

          {/* Desglose por tramos (si hay) */}
          {tramos.length > 0 && (
            <Section title="Desglose por tramo de consumo">
              <div className="overflow-x-auto">
              <table className="w-full min-w-[360px] text-xs">
                <thead className="bg-neutral-50 text-neutral-600">
                  <tr>
                    <th scope="col" className="text-left px-2 py-1">Tramo (m³)</th>
                    <th scope="col" className="text-right px-2 py-1">m³ usados</th>
                    <th scope="col" className="text-right px-2 py-1">Valor unitario</th>
                    <th scope="col" className="text-right px-2 py-1">Monto</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {tramos.map((t, i) => (
                    <tr key={i}>
                      <td className="px-2 py-1">
                        {t.desde ?? 0}–{t.hasta ?? "∞"}
                      </td>
                      <td className="text-right px-2 py-1 font-mono">{t.m3 ?? 0}</td>
                      <td className="text-right px-2 py-1 font-mono">
                        {formatCLP(t.valor_unitario ?? 0)}
                      </td>
                      <td className="text-right px-2 py-1 font-mono font-medium">
                        {formatCLP(t.monto ?? 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </Section>
          )}

          {/* Footer: total + vencimiento + barcode placeholder */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            <div className="bg-neutral-100 border border-neutral-300 p-3 text-center">
              <div className="text-xs text-neutral-600">CONSUMO ÚLTIMOS 13 MESES</div>
              <div className="text-xs text-neutral-500 italic mt-2">
                (gráfico se renderiza al emitir)
              </div>
            </div>
            <div className="space-y-2">
              <div className="bg-neutral-900 text-white text-center py-3 rounded">
                <div className="text-xs uppercase tracking-wider opacity-80">
                  TOTAL A PAGAR $
                </div>
                <div className="text-2xl font-bold">{formatCLP(lectura.monto_total).replace("$", "").trim()}</div>
              </div>
              <div className="bg-white border border-neutral-300 text-center py-2">
                <div className="text-xs uppercase tracking-wider text-neutral-600">
                  Fecha vencimiento
                </div>
                <div className="text-sm font-bold">— al emitir —</div>
              </div>
              <div className="bg-neutral-50 border border-dashed border-neutral-300 text-center py-2 text-xs text-neutral-500 italic">
                Código de barras se asigna al facturar
              </div>
            </div>
          </div>

          {empresa.mensaje_boleta && (
            <div className="bg-amber-50 border-l-4 border-amber-300 px-3 py-2 text-xs text-amber-900">
              <strong>MENSAJE:</strong> {empresa.mensaje_boleta}
            </div>
          )}

          {lectura.alertas.length > 0 && (
            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 space-y-1">
              <div className="text-xs font-semibold text-amber-900 flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" />
                Alertas detectadas en esta proforma
              </div>
              <ul className="list-disc list-inside text-xs text-amber-800 space-y-0.5">
                {lectura.alertas.map((a) => (
                  <li key={a.codigo}>{a.descripcion}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="text-xs text-neutral-500 italic flex items-center gap-1 pt-2 border-t border-neutral-100">
            <CheckCircle2 className="h-3 w-3" strokeWidth={1.5} />
            Solución de Factura Electrónica de: InnovaClarke SSR
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="border border-neutral-300 rounded">
      <div className="bg-neutral-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-neutral-700 border-b border-neutral-300">
        {title}
      </div>
      {children}
    </div>
  );
}

function Field({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: string;
  valueClassName?: string;
}): JSX.Element {
  return (
    <div className="flex gap-1">
      <span className="font-semibold text-neutral-700">{label}</span>
      <span className="text-neutral-500">:</span>
      <span className={valueClassName ?? "text-neutral-900"}>{value}</span>
    </div>
  );
}

function CuentaLine({
  label,
  valor,
  bold,
  highlight,
  negativo,
}: {
  label: string;
  valor: number;
  bold?: boolean;
  highlight?: boolean;
  negativo?: boolean;
}): JSX.Element {
  return (
    <div
      className={`flex justify-between border-b border-dotted border-neutral-200 pb-0.5 ${
        highlight ? "bg-neutral-900 text-white px-2 py-1 rounded" : ""
      } ${bold ? "font-bold" : ""}`}
    >
      <span>{label}</span>
      <span className={`font-mono ${negativo ? "text-emerald-700" : ""}`}>
        {formatCLP(valor)}
      </span>
    </div>
  );
}

function SuministroLine({
  label,
  value,
  extraLabel,
  extraValue,
}: {
  label: string;
  value: string;
  extraLabel?: string;
  extraValue?: string;
}): JSX.Element {
  return (
    <div className="flex justify-between">
      <span>
        <strong>{label}:</strong> {value}
      </span>
      {extraLabel && (
        <span>
          <strong>{extraLabel}:</strong> {extraValue}
        </span>
      )}
    </div>
  );
}

// Re-export por conveniencia para que el componente de detalle pueda mostrar
// el badge de alertas con el mismo formato.
export type _ProformaResponse = ProformaResponse;
